import { TextField, type TextFieldProps, type TextFieldVariants } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  Controller,
  type ControllerFieldState,
  type ControllerProps,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';

interface NumericTextFieldControllerProps<
  // textField generics
  TTextFieldVariant extends TextFieldVariants,
  // controller generics
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends Omit<ControllerProps<TFieldValues, TName>, 'render'> {
  label?: TextFieldProps<TTextFieldVariant>['label'];
  slotProps?: {
    textField?: Partial<Omit<TextFieldProps<TTextFieldVariant>, 'type' | 'inputMode'>>;
  };
}

// Parse the raw input (allowing a comma decimal separator) into a number, or
// null when it is not yet a valid number (empty, "-", "1." mid-typing, ...).
const toNumberOrNull = (raw: string): number | null => {
  const value = parseFloat(raw.replace(',', '.'));
  return Number.isNaN(value) ? null : value;
};

const toDisplayText = (value: unknown): string =>
  value || value === 0 ? String(value) : '';

// Keeps the raw text locally while typing so intermediate states like "1." or
// "-" survive re-renders, while always handing a `number | null` back to RHF.
// This avoids storing a string in the form state, which made `z.number()`
// validators fail with "expected number, received string" on every keystroke.
const NumericFieldInput = <
  TTextFieldVariant extends TextFieldVariants,
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  field,
  fieldState,
  label,
  slotProps,
}: {
  field: ControllerRenderProps<TFieldValues, TName>;
  fieldState: ControllerFieldState;
  label?: TextFieldProps<TTextFieldVariant>['label'];
  slotProps?: NumericTextFieldControllerProps<
    TTextFieldVariant,
    TFieldValues,
    TName
  >['slotProps'];
}) => {
  const { onChange, value, ...rest } = field;
  const [text, setText] = useState(() => toDisplayText(value));

  // Resync the displayed text when the form value changes from the outside
  // (reset / defaultValues load), but not while the user is typing — there the
  // local text is the source of truth and already matches `value`.
  useEffect(() => {
    if (toNumberOrNull(text) !== (value ?? null)) {
      setText(toDisplayText(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <TextField
      {...rest}
      value={text}
      label={label}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error?.message}
      inputMode="decimal"
      onChange={(e) => {
        setText(e.target.value);
        onChange(toNumberOrNull(e.target.value));
      }}
      onBlur={(e) => {
        const parsed = toNumberOrNull(e.target.value);
        setText(parsed === null ? '' : String(parsed));
        onChange(parsed);
        field.onBlur();
      }}
      {...slotProps?.textField}
    />
  );
};

export const NumericTextFieldController = <
  TTextFieldVariant extends TextFieldVariants = TextFieldVariants,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: NumericTextFieldControllerProps<TTextFieldVariant, TFieldValues, TName>,
) => {
  const { label, slotProps, ...controllerProps } = props;
  return (
    <Controller
      {...controllerProps}
      render={({ field, fieldState }) => (
        <NumericFieldInput<TTextFieldVariant, TFieldValues, TName>
          field={field}
          fieldState={fieldState}
          label={label}
          slotProps={slotProps}
        />
      )}
    />
  );
};
